(() => {
  'use strict';
  if (customElements.get('fpb-product-tabs')) return;

  class FPBProductTabs extends HTMLElement {
    connectedCallback() {
      // Defer one microtask so editor-inserted children are available.
      queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }

    initialize() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.nav = this.querySelector('.fpbt__nav');
      if (!this.nav) { this.controller = null; return; }
      this.nav.replaceChildren();
      this.nav.setAttribute('role', 'tablist');
      this.nav.setAttribute('aria-label', this.dataset.label || 'Product information');
      const editor = this.dataset.editor === 'true';
      this.items = Array.from(this.querySelectorAll('.fpbt__item')).map(wrapper => {
        const details = wrapper.querySelector('.fpbt__details');
        if (!details) return null;
        const summary = details.querySelector('.fpbt__summary');
        const panel = details.querySelector('.fpbt__panel');
        if (!summary || !panel) return null;
        const hasContent = panel.textContent.trim() || panel.querySelector('img,video,iframe,table,[data-fpbt-video],input,button,canvas,svg,model-viewer,.shopify-app-block');
        wrapper.hidden = !editor && !hasContent;
        if (wrapper.hidden) return null;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fpbt__button';
        button.id = `${panel.id}-tab`;
        button.textContent = summary.querySelector('[data-fpbt-title]').textContent;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        summary.setAttribute('aria-controls', panel.id);
        this.nav.append(button);
        return { wrapper, details, summary, panel, button };
      }).filter(Boolean);
      this.hidden = !editor && !this.items.length;
      if (!this.items.length) return;
      const previous = this.items.findIndex(item => item.panel.id === this.activePanel);
      this.active = previous < 0 ? 0 : previous;
      this.mobile = matchMedia('(max-width: 767px)');
      this.modeChange = () => this.setMode();
      this.mobile.addEventListener('change', this.modeChange);
      this.addEventListener('click', event => this.onClick(event), { ...options, capture: true });
      this.nav.addEventListener('keydown', event => this.onKeydown(event), options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const index = this.items.findIndex(item => item.wrapper === event.target || item.wrapper.contains(event.target));
        if (index >= 0) this.activate(index);
      }, options);
      this.setMode();
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.mobile?.removeEventListener('change', this.modeChange);
    }

    setMode() {
      this.dataset.mode = this.mobile.matches ? 'mobile' : 'desktop';
      this.nav.hidden = this.mobile.matches;
      this.items.forEach(item => {
        item.panel.setAttribute('role', this.mobile.matches ? 'region' : 'tabpanel');
        item.panel.setAttribute('aria-labelledby', this.mobile.matches ? item.summary.id : item.button.id);
        if (this.mobile.matches) item.panel.removeAttribute('tabindex');
        else item.panel.tabIndex = 0;
      });
      this.activate(this.active);
    }

    activate(index, collapse = false) {
      this.active = index;
      this.activePanel = this.items[index].panel.id;
      this.items.forEach((item, i) => {
        const open = i === index && !collapse;
        item.details.open = open;
        item.panel.hidden = !open;
        item.summary.setAttribute('aria-expanded', String(open));
        item.button.setAttribute('aria-selected', String(i === index));
        item.button.tabIndex = i === index ? 0 : -1;
        if (!open) this.stopMedia(item.panel);
      });
    }

    onClick(event) {
      const play = event.target.closest('[data-fpbt-play]');
      if (play && this.contains(play)) { this.playVideo(play); return; }
      const control = event.target.closest('.fpbt__button,.fpbt__summary');
      if (!control || !this.contains(control)) return;
      const index = this.items.findIndex(item => item.button === control || item.summary === control);
      if (index < 0) return;
      event.preventDefault();
      const collapse = this.mobile.matches && control === this.items[index].summary && this.items[index].details.open;
      this.activate(index, collapse);
    }

    onKeydown(event) {
      const index = this.items.findIndex(item => item.button === event.target);
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const rtl = getComputedStyle(this).direction === 'rtl';
      let next = index;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.items.length - 1;
      else {
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        next = (index + direction * (rtl ? -1 : 1) + this.items.length) % this.items.length;
      }
      this.activate(next);
      this.items[next].button.focus();
    }

    playVideo(button) {
      const container = button.closest('[data-fpbt-video]');
      const { provider, videoId, videoTitle } = container.dataset;
      if (container.querySelector('iframe')) return;
      let source;
      if (provider === 'youtube' && /^[a-zA-Z0-9_-]+$/.test(videoId)) {
        source = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
      } else if (provider === 'vimeo' && /^\d+$/.test(videoId)) {
        source = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      } else return;
      const iframe = document.createElement('iframe');
      iframe.title = videoTitle || 'Product video';
      iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.src = source;
      button.hidden = true;
      container.append(iframe);
      iframe.focus();
    }

    stopMedia(panel) {
      panel.querySelectorAll('video').forEach(video => { if (!video.paused) video.pause(); });
      panel.querySelectorAll('[data-fpbt-video]').forEach(container => {
        container.querySelector('iframe')?.remove();
        const poster = container.querySelector('[data-fpbt-play]');
        if (poster) poster.hidden = false;
      });
    }
  }
  customElements.define('fpb-product-tabs', FPBProductTabs);
})();